import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import HistoryIcon from '@mui/icons-material/History'
import { useNotify, useTranslate } from 'react-admin'
import { themeColor } from '../../layout/theme/color'
import type { LastImportOut, PreviewResult } from '../../types/apiRequests'
import { getLastImport, previewBundle } from './api'
import ExportDialog from './ExportDialog'
import ImportPreviewDialog from './ImportPreviewDialog'
import LastImportCard from './LastImportCard'

const SectionHeading: FC<{
  title: string
  description?: string
  icon: React.ReactNode
}> = ({ title, description, icon }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '10px',
        bgcolor: themeColor.blue10,
        color: themeColor.blue,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: themeColor.slate90 }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: themeColor.slate60, mt: 0.25 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  </Stack>
)

const SectionCard: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      p: 3,
      borderRadius: '14px',
      border: `1px solid ${themeColor.slate10}`,
      bgcolor: '#FFFFFF',
    }}
  >
    {children}
  </Box>
)

const DropZone: FC<{
  onFile: (file: File) => void
  busy: boolean
}> = ({ onFile, busy }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file.name.toLowerCase().endsWith('.json')) {
      notify(translate('portalSettings.importExport.import.invalidFile'), {
        type: 'error',
      })
      return
    }
    onFile(file)
  }

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault()
        if (!busy) setDragActive(true)
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragActive(false)
        if (!busy) handleFiles(e.dataTransfer.files)
      }}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !busy) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={translate('portalSettings.importExport.import.dropzone')}
      sx={{
        cursor: busy ? 'progress' : 'pointer',
        border: `1.5px dashed ${dragActive ? themeColor.blue : themeColor.slate20}`,
        bgcolor: dragActive ? themeColor.blue10 : themeColor.slate05,
        borderRadius: '14px',
        px: 4,
        py: 5,
        textAlign: 'center',
        transition: 'all 0.2s ease',
        opacity: busy ? 0.6 : 1,
        ['&:hover']: busy
          ? {}
          : {
            borderColor: themeColor.blue60,
            bgcolor: themeColor.blue10,
          },
        ['&:focus-visible']: {
          outline: `2px solid ${themeColor.blue60}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: '#FFFFFF',
          border: `1px solid ${themeColor.slate10}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 1.5,
          color: dragActive ? themeColor.blue : themeColor.slate60,
          transition: 'all 0.2s ease',
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 24 }} />
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: dragActive ? themeColor.blue : themeColor.slate60,
          fontWeight: 500,
        }}
      >
        {busy
          ? translate('portalSettings.importExport.import.previewing')
          : dragActive
            ? translate('portalSettings.importExport.import.dropzoneActive')
            : translate('portalSettings.importExport.import.dropzone')}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />
    </Box>
  )
}

const ImportExportSection: FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const [exportOpen, setExportOpen] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [previewFile, setPreviewFile] = useState<File | null>(null)
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null)
  const [lastImport, setLastImport] = useState<LastImportOut | null>(null)

  const refreshLastImport = useCallback(async () => {
    try {
      setLastImport(await getLastImport())
    } catch {
      setLastImport(null)
    }
  }, [])

  useEffect(() => {
    void refreshLastImport()
  }, [refreshLastImport])

  const handleFile = async (file: File) => {
    setPreviewing(true)
    try {
      const preview = await previewBundle(file)
      setPreviewFile(file)
      setPreviewResult(preview)
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Preview failed', {
        type: 'error',
      })
    } finally {
      setPreviewing(false)
    }
  }

  const closePreview = (didImport: boolean) => {
    setPreviewFile(null)
    setPreviewResult(null)
    if (didImport) void refreshLastImport()
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: themeColor.slate60, mb: 3 }}>
        {translate('portalSettings.importExport.description')}
      </Typography>

      <Stack spacing={2.5}>
        <SectionCard>
          <SectionHeading
            title={translate('portalSettings.importExport.export.title')}
            description={translate('portalSettings.importExport.export.description')}
            icon={<CloudDownloadOutlinedIcon />}
          />
          <Button
            variant="contained"
            startIcon={<CloudDownloadOutlinedIcon />}
            onClick={() => setExportOpen(true)}
          >
            {translate('portalSettings.importExport.export.button')}
          </Button>
        </SectionCard>

        <SectionCard>
          <SectionHeading
            title={translate('portalSettings.importExport.import.title')}
            description={translate('portalSettings.importExport.import.description')}
            icon={<CloudUploadOutlinedIcon />}
          />
          <DropZone
            onFile={(f) => {
              void handleFile(f)
            }}
            busy={previewing}
          />
        </SectionCard>

        <SectionCard>
          <SectionHeading
            title={translate('portalSettings.importExport.lastImport.title')}
            description={translate('portalSettings.importExport.lastImport.description')}
            icon={<HistoryIcon />}
          />
          <LastImportCard data={lastImport} />
        </SectionCard>
      </Stack>

      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      <ImportPreviewDialog
        file={previewFile}
        preview={previewResult}
        onClose={closePreview}
      />
    </Box>
  )
}

export default ImportExportSection
